import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './hooks/useAuth';
import { ProtectedRoute, PublicRoute } from './components/Auth/RouteGuards';
import { ThemeProvider } from './theme/ThemeProvider';
import './App.css';
import Home from './pages/Home';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import ProfileWork from './pages/ProfileWork';
import ProfileNotifications from './pages/ProfileNotifications';
import ProfileSettings from './pages/ProfileSettings';
import Admin from './pages/Admin';
import MessagePage from './pages/MessagePage';
import SSOCallback from './pages/SSOCallback';
import VerifyPage from './pages/VerifyPage';
import Evaluations from './pages/Evaluations';
import Submission from './pages/Submission';
import ResetPage from './pages/ResetPage';
import CompetitionPage from './pages/Competition';
import ManageCompetitionPage from './pages/manage/ManageCompetitionPage';
import PresentPage from './pages/present/PresentPage';
import PublicCompetition from './pages/PublicCompetition';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Support from './pages/Support';
import CliApprove from './pages/CliApprove';
import config from './config/environment';
import NotificationBanner from './components/common/NotificationBanner';
import TourProvider from './components/tour/TourProvider';


/**
 * Main App Component
 */
function App() {
  console.log('API Base URL:', config.API_BASE_URL);
  console.log('App Name:', config.APP_NAME);
  console.log('Version:', config.VERSION);
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <TourProvider>
          <div className="App">
            <NotificationBanner bannerKey="maintenance" config={config?.UI?.BANNERS?.maintenance} />
            <NotificationBanner bannerKey="announcement" config={config?.UI?.BANNERS?.announcement} />
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Home />} />
              <Route 
                path="/login" 
                element={
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                } 
              />
              
              {/* Message and Verification Routes */}
              <Route path="/message" element={<MessagePage />} />
              <Route path="/sso-callback" element={<SSOCallback />} />
              <Route path="/verify" element={<VerifyPage />} />
              <Route path="/reset" element={<ResetPage />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/support" element={<Support />} />
              <Route path="/public/competition/:id" element={<PublicCompetition />} />
              <Route path="/unauthorized" element={<MessagePage 
                    title="Access Denied"
                    message="You do not have permission to view this page."
                    type="error"
                    showCloseButton={true}
              />} />            
              {/* Protected Routes */}
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/profile" 
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/profile/work"
                element={
                  <ProtectedRoute>
                    <ProfileWork />
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/profile/notifications"
                element={
                  <ProtectedRoute>
                    <ProfileNotifications />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile/settings"
                element={
                  <ProtectedRoute>
                    <ProfileSettings />
                  </ProtectedRoute>
                }
              />
              <Route 
                path="/profile/joined" 
                element={
                  <ProtectedRoute>
                    <Navigate to="/profile/work?tab=joined" replace />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/profile/evaluations" 
                element={
                  <ProtectedRoute>
                    <Navigate to="/profile/work?tab=evaluations" replace />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/profile/hosted" 
                element={
                  <ProtectedRoute>
                    <Navigate to="/profile/work?tab=hosted" replace />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin" 
                element={
                  <ProtectedRoute>
                    <Admin />
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/competition/:id"
                element={
                  <ProtectedRoute>
                    <CompetitionPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/evaluations/:id"
                element={
                  <ProtectedRoute>
                      <Evaluations />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manage/:id/present"
                element={
                  <ProtectedRoute>
                    <PresentPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manage/:id"
                element={
                  <ProtectedRoute>
                    <ManageCompetitionPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/submission/:id"
                element={
                  <ProtectedRoute>
                      <Submission />
                  </ProtectedRoute>
                }
              />
              {/* CLI login approval: reached from the link `ikompete login` prints; not in navigation. */}
              <Route
                path="/cli"
                element={
                  <ProtectedRoute>
                    <CliApprove />
                  </ProtectedRoute>
                }
              />
              {/* Catch all route - redirect to home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
          </TourProvider>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
